// ============================================
// UI - User interface rendering and interaction
// ============================================

import {
  AppState,
  WeekSchedule,
  DaySchedule,
  DayAvailability,
  AlenaShift,
  IlyaShift,
  ALENA_SHIFT_LABELS,
  ILYA_SHIFT_LABELS,
  SHIFT_FULL_NAMES,
} from "./types.js";

import {
  calculateWeekAvailability,
  formatDateShort,
  getDayName,
  getNextWeekStart,
  getPrevWeekStart,
} from "./shifts-logic.js";

import {
  loadState,
  getOrCreateWeek,
  updateWeekCache,
  setCurrentWeek,
  getForceMode,
  setForceMode,
} from "./storage.js";

import {
  toggleShift as apiToggleShift,
  toggleKindergarten as apiToggleKindergarten,
  logout,
} from "./api.js";

import { clearAuth } from "./auth.js";

// ============================================
// STATE
// ============================================

let state: AppState;
let currentWeek: WeekSchedule | null = null;
let forceMode = false;

// ============================================
// DOM HELPERS
// ============================================

function $(selector: string): HTMLElement | null {
  return document.querySelector(selector);
}

function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text) el.textContent = text;
  return el;
}

// ============================================
// RENDER FUNCTIONS
// ============================================

async function renderWeekNav(): Promise<void> {
  const nav = $("#week-nav");
  if (!nav) return;

  try {
    const week = await getOrCreateWeek(state, state.currentWeekStart);
    currentWeek = week;

    const startDate = formatDateShort(week.days[0].date);
    const endDate = formatDateShort(week.days[6].date);

    nav.innerHTML = `
      <button class="nav-btn" id="prev-week">&lt; Prev</button>
      <span class="week-label">Неделя ${startDate} - ${endDate}</span>
      <button class="nav-btn" id="next-week">Next &gt;</button>
    `;

    $("#prev-week")?.addEventListener("click", () => {
      setCurrentWeek(state, getPrevWeekStart(state.currentWeekStart));
      render();
    });

    $("#next-week")?.addEventListener("click", () => {
      setCurrentWeek(state, getNextWeekStart(state.currentWeekStart));
      render();
    });
  } catch (e) {
    console.error("Failed to render week navigation:", e);
    nav.innerHTML = `<div class="error">Failed to load week data. Please check backend connection.</div>`;
  }
}

function createShiftCell(
  isSelected: boolean,
  canWork: boolean,
  exists: boolean | undefined,
  blockedReason: string | undefined,
  label: string,
  fullName: string,
  onClick: () => void,
): HTMLElement {
  const cell = createElement("button", "shift-cell");
  cell.textContent = label;
  cell.title = blockedReason || fullName;

  // A selected shift represents the actual schedule and must stay visually
  // selected even if another forced shift makes it unavailable by the rules.
  if (isSelected) {
    cell.classList.add("selected", "working");
  } else if (!canWork) {
    // Non-existent shifts get different styling
    if (exists === false) {
      cell.classList.add("not-exists");
    } else {
      cell.classList.add("blocked");
    }
    if (forceMode) {
      cell.classList.add("force-override");
    }
  } else {
    cell.classList.add("available");
  }

  if (canWork || isSelected || forceMode) {
    cell.addEventListener("click", onClick);
  }

  return cell;
}

function renderAlenaShifts(
  day: DaySchedule,
  availability: DayAvailability,
  dayIndex: number,
): HTMLElement {
  const container = createElement("div", "shifts-group alena-shifts");

  const shifts: AlenaShift[] = ["earlyMorning", "morning", "evening"];

  shifts.forEach((shift) => {
    const avail = availability.alena[shift];
    const isSelected = day.alena[shift];

    const cell = createShiftCell(
      isSelected,
      avail.canWork,
      avail.exists,
      avail.blockedReason,
      ALENA_SHIFT_LABELS[shift],
      SHIFT_FULL_NAMES[shift],
      () => handleAlenaShiftClick(dayIndex, shift),
    );

    container.appendChild(cell);
  });

  return container;
}

function renderIlyaShifts(
  day: DaySchedule,
  availability: DayAvailability,
  dayIndex: number,
): HTMLElement {
  const container = createElement("div", "shifts-group ilya-shifts");

  const shifts: IlyaShift[] = ["earlyMorning", "evening", "night"];

  shifts.forEach((shift) => {
    const avail = availability.ilya[shift];
    const isSelected = day.ilya[shift];

    const cell = createShiftCell(
      isSelected,
      avail.canWork,
      avail.exists,
      avail.blockedReason,
      ILYA_SHIFT_LABELS[shift],
      SHIFT_FULL_NAMES[shift],
      () => handleIlyaShiftClick(dayIndex, shift),
    );

    container.appendChild(cell);
  });

  return container;
}

function renderKindergartenCell(
  day: DaySchedule,
  availability: DayAvailability,
  dayIndex: number,
): HTMLElement {
  const cell = createElement("button", "kindergarten-cell");
  cell.textContent = "G";
  cell.title = day.kindergarten ? "Садик работает" : "Садика нет";

  if (day.kindergarten) {
    cell.classList.add("active");
  } else {
    cell.classList.add("inactive");
  }

  // Show default indicator
  if (day.kindergarten === availability.kindergartenDefault) {
    cell.classList.add("default");
  }

  cell.addEventListener("click", () => handleKindergartenClick(dayIndex));

  return cell;
}

function renderScheduleTable(): void {
  const container = $("#schedule-table");
  if (!container || !currentWeek) return;

  const week = currentWeek;
  const availability = calculateWeekAvailability(week);
  const today = new Date();
  const todayISO = today.toISOString().split("T")[0];

  container.innerHTML = "";

  // Header
  const header = createElement("div", "schedule-header");
  header.innerHTML = `
    <div class="header-cell date-header">День</div>
    <div class="header-cell alena-header">Алёна</div>
    <div class="header-cell ilya-header">Илья</div>
    <div class="header-cell aglaya-header">Аглая</div>
  `;
  container.appendChild(header);

  // Days
  week.days.forEach((day, index) => {
    const isToday = day.date === todayISO;
    const row = createElement("div", `schedule-row${isToday ? " current-day" : ""}`);
    const dayAvail = availability[index];

    // Date cell
    const dateCell = createElement("div", "date-cell");
    dateCell.innerHTML = `
      <span class="day-name">${getDayName(day.date)}</span>
      <span class="day-date">${formatDateShort(day.date)}</span>
    `;
    row.appendChild(dateCell);

    // Alena shifts
    const alenaCell = createElement("div", "person-cell");
    alenaCell.appendChild(renderAlenaShifts(day, dayAvail, index));
    row.appendChild(alenaCell);

    // Ilya shifts
    const ilyaCell = createElement("div", "person-cell");
    ilyaCell.appendChild(renderIlyaShifts(day, dayAvail, index));
    row.appendChild(ilyaCell);

    // Kindergarten
    const aglayaCell = createElement(
      "div",
      "person-cell kindergarten-container",
    );
    aglayaCell.appendChild(renderKindergartenCell(day, dayAvail, index));
    row.appendChild(aglayaCell);

    container.appendChild(row);
  });
}

// ============================================
// EVENT HANDLERS
// ============================================

async function handleAlenaShiftClick(
  dayIndex: number,
  shift: AlenaShift,
): Promise<void> {
  if (!currentWeek) return;

  const day = currentWeek.days[dayIndex];
  const availability = calculateWeekAvailability(currentWeek);
  const canWork = availability[dayIndex].alena[shift].canWork;
  if (!canWork && !day.alena[shift] && !forceMode) return;

  try {
    // Toggle shift via API
    await apiToggleShift(day.date, "alena", shift);

    // Update local state
    day.alena[shift] = !day.alena[shift];
    updateWeekCache(state, currentWeek);

    // Re-render table
    renderScheduleTable();
  } catch (e) {
    console.error("Failed to toggle shift:", e);
    alert("Failed to update shift. Please try again.");
  }
}

async function handleIlyaShiftClick(
  dayIndex: number,
  shift: IlyaShift,
): Promise<void> {
  if (!currentWeek) return;

  const day = currentWeek.days[dayIndex];
  const availability = calculateWeekAvailability(currentWeek);
  const canWork = availability[dayIndex].ilya[shift].canWork;
  if (!canWork && !day.ilya[shift] && !forceMode) return;

  try {
    // Toggle shift via API
    await apiToggleShift(day.date, "ilya", shift);

    // Update local state
    day.ilya[shift] = !day.ilya[shift];
    updateWeekCache(state, currentWeek);

    // Re-render table
    renderScheduleTable();
  } catch (e) {
    console.error("Failed to toggle shift:", e);
    alert("Failed to update shift. Please try again.");
  }
}

async function handleKindergartenClick(dayIndex: number): Promise<void> {
  if (!currentWeek) return;

  const day = currentWeek.days[dayIndex];

  try {
    // Toggle kindergarten via API
    await apiToggleKindergarten(day.date);

    // Update local state
    day.kindergarten = !day.kindergarten;
    updateWeekCache(state, currentWeek);

    // Re-render table
    renderScheduleTable();
  } catch (e) {
    console.error("Failed to toggle kindergarten:", e);
    alert("Failed to update kindergarten. Please try again.");
  }
}

// ============================================
// MAIN RENDER
// ============================================

async function render(): Promise<void> {
  await renderWeekNav();
  renderScheduleTable();
}

// ============================================
// SWIPE GESTURES FOR MOBILE
// ============================================

let touchStartX = 0;
let touchEndX = 0;

function handleSwipeGesture(): void {
  const swipeThreshold = 50; // minimum distance for swipe
  const diff = touchStartX - touchEndX;

  if (Math.abs(diff) < swipeThreshold) return;

  if (diff > 0) {
    // Swiped left - next week
    setCurrentWeek(state, getNextWeekStart(state.currentWeekStart));
    render();
  } else {
    // Swiped right - prev week
    setCurrentWeek(state, getPrevWeekStart(state.currentWeekStart));
    render();
  }
}

function initSwipeGestures(): void {
  const table = $("#schedule-table");
  if (!table) return;

  table.addEventListener("touchstart", (e: Event) => {
    const touchEvent = e as TouchEvent;
    touchStartX = touchEvent.changedTouches[0].screenX;
  });

  table.addEventListener("touchend", (e: Event) => {
    const touchEvent = e as TouchEvent;
    touchEndX = touchEvent.changedTouches[0].screenX;
    handleSwipeGesture();
  });
}

// ============================================
// HAMBURGER MENU
// ============================================

/**
 * Initialize hamburger menu toggle
 */
function initHamburgerMenu(): void {
  const menuToggle = document.getElementById('menu-toggle');
  const dropdownMenu = document.getElementById('dropdown-menu');

  if (!menuToggle || !dropdownMenu) return;

  menuToggle.addEventListener('click', (e) => {
    e.stopPropagation();
    menuToggle.classList.toggle('active');
    dropdownMenu.classList.toggle('active');
  });

  // Close menu when clicking outside
  document.addEventListener('click', (e) => {
    if (!menuToggle.contains(e.target as Node) && !dropdownMenu.contains(e.target as Node)) {
      menuToggle.classList.remove('active');
      dropdownMenu.classList.remove('active');
    }
  });

  // Close menu on ESC key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      menuToggle.classList.remove('active');
      dropdownMenu.classList.remove('active');
    }
  });
}

// ============================================
// FORCE MODE (allow setting blocked/unavailable shifts)
// ============================================

/**
 * Read force mode from URL param (?force=1 / ?force=0) or persisted setting.
 * A URL param, when present, also updates the persisted setting.
 */
function initForceMode(): void {
  const params = new URLSearchParams(window.location.search);
  if (params.has("force")) {
    forceMode = params.get("force") !== "0";
    setForceMode(forceMode);
  } else {
    forceMode = getForceMode();
  }

  const toggle = document.getElementById(
    "force-mode-toggle",
  ) as HTMLInputElement | null;
  if (!toggle) return;

  toggle.checked = forceMode;
  toggle.addEventListener("change", () => {
    forceMode = toggle.checked;
    setForceMode(forceMode);
    renderScheduleTable();
  });
}

// ============================================
// INITIALIZATION
// ============================================

function initLogout(): void {
  const logoutBtn = document.getElementById('logout-btn');
  if (!logoutBtn) return;

  logoutBtn.addEventListener('click', async () => {
    await logout();
    clearAuth();
    window.location.reload();
  });
}

export async function initApp(): Promise<void> {
  state = loadState();
  initForceMode();
  await render();
  initSwipeGestures();
  initHamburgerMenu();
  initLogout();
}
